import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AppDatePicker,
  AppInput,
  AppSelect,
  AppTextarea,
  OutlineButton,
  PrimaryButton,
} from '../../../../components/ui';
import { VEHICLE_CLASSES } from '../../../../data/master/equipments';
import { createEquipment } from '../../../../repositories/equipment.repository';
import { lookupEquipmentTypes } from '../../../../repositories/lookup.repository';

const empty = {
  equipmentType: '',
  equipmentDescription: '',
  remarks: '',
  vehicleClass: 'Construction Equipment',
  manufacturer: '',
  model: '',
  variant: '',
  capacity: '',
  capacityUom: '',
  yom: '',
  regNo: '',
  chassisNumber: '',
  engineNumber: '',
  dateOfReg: '',
  regValidUpto: '',
  nameAsPerRc: '',
  fuel: 'Diesel',
  color: '',
  insuranceValidUpto: '',
  fitnessValidUpto: '',
  pucValidUpto: '',
  taxValidUpto: '',
  status: 'Verified',
};

export function VendorAssetFormModal({ isOpen, onClose, vendor, person, vendorId, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const resolvedVendor = vendor || null;
  const resolvedPerson = person || null;
  const isVillager = Boolean(resolvedPerson?.id);
  const ownerName = isVillager
    ? resolvedPerson.fullName || resolvedPerson.name || ''
    : resolvedVendor?.tradeAgencyName || '';

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      ...empty,
      nameAsPerRc: ownerName,
    });
    setErrors({});
    let cancelled = false;
    (async () => {
      try {
        const items = await lookupEquipmentTypes();
        if (!cancelled) setEquipmentTypes(items || []);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load equipment types',
            status: 'error',
            position: 'top-right',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, ownerName, toast]);

  const equipmentOptions = useMemo(
    () =>
      equipmentTypes
        .filter((e) => e.active !== false)
        .map((e) => ({
          value: e.name,
          label: e.name,
          description: e.vehicleClass,
          id: e.id,
        })),
    [equipmentTypes],
  );

  const patch = (partial) => {
    setForm((f) => ({ ...f, ...partial }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(partial).forEach((k) => delete next[k]);
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.equipmentType) next.equipmentType = 'Required';
    if (!form.manufacturer.trim()) next.manufacturer = 'Required';
    if (!form.regNo.trim()) next.regNo = 'Required';
    setErrors(next);
    if (Object.keys(next).length) {
      toast({ title: 'Fix highlighted fields', status: 'warning', position: 'top-right' });
      return;
    }
    if (!resolvedVendor?.id && !resolvedPerson?.id) {
      toast({ title: 'Missing owner id', status: 'error', position: 'top-right' });
      return;
    }
    const typeRow = equipmentTypes.find((t) => t.name === form.equipmentType);
    if (!typeRow?.id) {
      toast({ title: 'Invalid equipment type', status: 'error', position: 'top-right' });
      return;
    }

    setSaving(true);
    try {
      const asset = await createEquipment({
        ownerType: isVillager ? 'Villager' : 'Vendor',
        peopleDbId: isVillager ? resolvedPerson.id : null,
        vendorDbId: isVillager ? null : resolvedVendor.id,
        villageDbId: isVillager
          ? resolvedPerson.villageDbId || null
          : resolvedVendor.villageDbId || null,
        villageId: isVillager
          ? resolvedPerson.villageId || ''
          : resolvedVendor.villageId || '',
        equipmentTypeId: typeRow.id,
        equipmentType: form.equipmentType,
        equipmentDescription:
          form.equipmentDescription.trim() || form.equipmentType.trim(),
        remarks: form.remarks.trim(),
        vehicleClass: form.vehicleClass,
        manufacturer: form.manufacturer.trim(),
        model: form.model.trim(),
        variant: form.variant.trim(),
        capacity: form.capacity.trim(),
        capacityUom: form.capacityUom.trim(),
        yom: form.yom.trim(),
        regNo: form.regNo.trim().toUpperCase(),
        chassisNumber: form.chassisNumber.trim(),
        engineNumber: form.engineNumber.trim(),
        dateOfReg: form.dateOfReg,
        regValidUpto: form.regValidUpto,
        nameAsPerRc: form.nameAsPerRc.trim() || ownerName,
        sonOfWifeOf: '',
        color: form.color.trim(),
        fuel: form.fuel.trim() || 'Diesel',
        pucDate: '',
        pucValidUpto: form.pucValidUpto,
        insuranceIssueDate: '',
        insuranceValidUpto: form.insuranceValidUpto,
        taxValidUpto: form.taxValidUpto,
        fitnessValidUpto: form.fitnessValidUpto,
        status: form.status,
        operationalStatus: 'Available',
      });
      toast({
        title: 'Equipment added',
        description: `${asset.assetId} · ${asset.equipmentType} · Available`,
        status: 'success',
        position: 'top-right',
      });
      onSaved?.(asset);
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to add equipment',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      isCentered
      scrollBehavior="inside"
      // Allow date popovers (ported to body) to receive focus / clicks
      trapFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader>
          {isVillager ? 'Add villager equipment' : 'Add vendor equipment'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" pb={4}>
          <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <AppSelect
                label="Equipment type"
                isRequired
                isSearchable
                value={form.equipmentType}
                onChange={(v) => {
                  const eq = equipmentTypes.find((e) => e.name === v);
                  patch({
                    equipmentType: v || '',
                    vehicleClass: eq?.vehicleClass || form.vehicleClass,
                    equipmentDescription:
                      form.equipmentDescription || (v ? v.toUpperCase() : ''),
                  });
                }}
                options={equipmentOptions}
                isInvalid={!!errors.equipmentType}
                errorMessage={errors.equipmentType}
              />
              <AppSelect
                label="Vehicle class"
                value={form.vehicleClass}
                onChange={(v) => patch({ vehicleClass: v })}
                options={VEHICLE_CLASSES.map((c) => ({ value: c, label: c }))}
              />
              <AppInput
                label="Equipment description"
                value={form.equipmentDescription}
                onChange={(e) => patch({ equipmentDescription: e.target.value })}
                placeholder="e.g. PICK & CARRY CRANE"
                helperText="Excel-style fleet description. Defaults to equipment type if blank."
              />
              <AppInput
                label="Make"
                isRequired
                value={form.manufacturer}
                onChange={(e) => patch({ manufacturer: e.target.value })}
                isInvalid={!!errors.manufacturer}
                errorMessage={errors.manufacturer}
              />
              <AppInput
                label="Model"
                value={form.model}
                onChange={(e) => patch({ model: e.target.value })}
              />
              <AppInput
                label="Reg. / Sr. No."
                isRequired
                value={form.regNo}
                onChange={(e) => patch({ regNo: e.target.value })}
                isInvalid={!!errors.regNo}
                errorMessage={errors.regNo}
              />
              <AppInput
                label="Name as per RC"
                value={form.nameAsPerRc}
                onChange={(e) => patch({ nameAsPerRc: e.target.value })}
              />
              <AppInput
                label="Capacity"
                value={form.capacity}
                onChange={(e) => patch({ capacity: e.target.value })}
              />
              <AppInput
                label="UOM"
                value={form.capacityUom}
                onChange={(e) => patch({ capacityUom: e.target.value })}
                placeholder="T / FT / CUM"
              />
              <AppInput
                label="YOM"
                value={form.yom}
                onChange={(e) =>
                  patch({ yom: e.target.value.replace(/\D/g, '').slice(0, 4) })
                }
              />
              <AppInput
                label="Fuel"
                value={form.fuel}
                onChange={(e) => patch({ fuel: e.target.value })}
              />
              <AppInput
                label="Chassis no"
                value={form.chassisNumber}
                onChange={(e) => patch({ chassisNumber: e.target.value })}
              />
              <AppInput
                label="Engine no"
                value={form.engineNumber}
                onChange={(e) => patch({ engineNumber: e.target.value })}
              />
              <AppDatePicker
                label="Date of Reg."
                value={form.dateOfReg}
                onChange={(e) => patch({ dateOfReg: e.target.value })}
              />
              <AppDatePicker
                label="Registration validity"
                value={form.regValidUpto}
                onChange={(e) => patch({ regValidUpto: e.target.value })}
              />
              <AppDatePicker
                label="Insurance valid up to"
                value={form.insuranceValidUpto}
                onChange={(e) => patch({ insuranceValidUpto: e.target.value })}
              />
              <AppDatePicker
                label="Fitness valid up to"
                value={form.fitnessValidUpto}
                onChange={(e) => patch({ fitnessValidUpto: e.target.value })}
              />
              <AppDatePicker
                label="PUC valid up to"
                value={form.pucValidUpto}
                onChange={(e) => patch({ pucValidUpto: e.target.value })}
              />
              <AppInput
                label="RTO Tax valid up to"
                value={form.taxValidUpto}
                onChange={(e) => patch({ taxValidUpto: e.target.value })}
                placeholder="Date or LTT"
              />
            </SimpleGrid>
            <AppTextarea
              label="Remarks"
              value={form.remarks}
              onChange={(e) => patch({ remarks: e.target.value })}
              placeholder="Optional notes from fleet register"
              rows={3}
            />
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>Save equipment</PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
